/**
 * ADR-0117 Slice C, Slice B security-review carry-over — classify a
 * shadow-decision row as "indeterminate" when the edge shadow path
 * could not faithfully evaluate the rule.
 *
 * Why: `apps/edge/src/mandate/dry_run_shadow.ts` stubs out two callback
 * surfaces the enforce path normally provides, so a shadow eval of a
 * rule that depends on either of them runs against an empty input and
 * may produce a "would-have-blocked" verdict that real enforcement
 * would NOT produce:
 *
 *   1. `rate_limit_binding` — returned as `undefined` for every binding
 *      name (the enforce path already decremented the merchant's quota
 *      for this mandate; double-counting in shadow would be a real bug,
 *      ADR-0117 §2). The handlers for r03 / r04 / r13 then fail with
 *      `evaluator_error: rate_limit_binding_missing`. In shadow the
 *      caller surfaces that as a would-have-block row, but the truth is
 *      we never asked the rate-limit binding what it would have said.
 *
 *   2. `derive_user_key` — intentionally omitted (no PII derivation, no
 *      salt access from the shadow module's closure — ADR-0117 §2). The
 *      r17 (per-customer scoping / BOLA) handler fails closed when the
 *      key can't be derived. Same posture as the rate-limit family:
 *      the shadow record exists, but it does NOT mean enforcement would
 *      block — it means shadow couldn't evaluate.
 *
 * The honest UX is to label these rows "indeterminate" instead of
 * presenting them as definitive blocks. The set is small and stable
 * (r03/r04/r13/r17); we map client-side from the `rule_id` the row
 * already carries. No wire/schema change is required.
 *
 * If/when the edge shadow path gains a side-effect-free rate-limit
 * "what-if" binding (a read-only ledger lookup that doesn't decrement),
 * those rule ids drop out of this set. Until then the honest label is
 * the right operator signal.
 */

import type { RuleId } from '@sill/types';

/**
 * The rule ids whose shadow evaluation runs against a stubbed callback
 * surface and therefore cannot produce a definitive verdict.
 *
 * Synchronised with the stubs in `apps/edge/src/mandate/dry_run_shadow.ts`
 * (`rate_limit_binding: () => undefined` + `derive_user_key` omitted).
 * A change there must be reflected here — keep the comments in sync.
 */
export const INDETERMINATE_SHADOW_RULE_IDS: ReadonlySet<string> = new Set([
  // rate_limit_binding stubs (the r03/r04/r13 family in the policy
  // catalog — `apps/dashboard/src/views/guardrails/catalog.ts`).
  'r03',
  'r04',
  'r13',
  // derive_user_key omission. r17 is `scope` category in the dashboard
  // catalog but the edge stub treats it the same as the rate-limit set
  // because the binding it needs is unavailable in shadow.
  'r17',
]);

/**
 * `true` when the shadow row's rule_id maps to a rule whose enforcement
 * binding is stubbed in the edge shadow path, so the verdict is not
 * definitive. The UI should label these rows "indeterminate" instead
 * of presenting them as a definitive would-have-blocked decision.
 */
export function isIndeterminateShadowRule(rule_id: RuleId): boolean {
  return INDETERMINATE_SHADOW_RULE_IDS.has(rule_id);
}
